/**
 * Calculate total FTE for a team based on their assigned activities
 */
export const calculateTeamFTE = (activities, globalInputs) => {
  if (!activities || activities.length === 0) return 0;

  // Import calculation logic from calculations.js
  const { calculateFTEModel } = require('./calculations');
  
  const result = calculateFTEModel(activities, globalInputs);
  return result.totalFTEs || 0;
};

/**
 * Aggregate FTEs from multiple teams
 */
export const aggregateTeamFTEs = (teams, activities, globalInputs) => {
  let totalFTE = 0;
  
  teams.forEach(team => {
    const teamActivities = activities.filter(a => 
      team.activities && team.activities.includes(a.id)
    );
    
    if (teamActivities.length > 0) {
      totalFTE += calculateTeamFTE(teamActivities, globalInputs);
    }
  });
  
  return totalFTE;
};

/**
 * Generate justification text for a team's FTE requirements
 */
export const generateTeamJustification = (team, activities, fteCount) => {
  const teamActivities = activities.filter(a => 
    team.activities && team.activities.includes(a.id)
  );
  
  const activityCount = teamActivities.length;
  const highRiskActivities = teamActivities.filter(a => a.risk >= 60).length;
  const complexActivities = teamActivities.filter(a => a.comp >= 60).length;
  
  let justification = `Team "${team.name}" requires ${fteCount.toFixed(2)} FTE to manage ${activityCount} EHS activities. `;
  
  if (highRiskActivities > 0) {
    justification += `${highRiskActivities} high-risk activities requiring specialized expertise. `;
  }
  
  if (complexActivities > 0) {
    justification += `${complexActivities} complex activities requiring advanced technical knowledge. `;
  }
  
  return justification.trim();
};

/**
 * Create condensed activity list for rollup views
 * Groups similar activities and highlights key risk areas
 */
export const condenseActivityList = (activities) => {
  if (!activities || activities.length === 0) return [];
  
  // Group by category or risk level
  const highRisk = activities.filter(a => a.risk >= 60);
  const mediumRisk = activities.filter(a => a.risk >= 26 && a.risk < 60);
  const lowRisk = activities.filter(a => a.risk < 26);
  
  const condensed = [];
  
  if (highRisk.length > 0) {
    condensed.push({
      category: 'High-Risk Activities',
      count: highRisk.length,
      examples: highRisk.slice(0, 3).map(a => a.name),
      totalHours: highRisk.reduce((sum, a) => sum + (a.baseHours || 0), 0)
    });
  }
  
  if (mediumRisk.length > 0) {
    condensed.push({
      category: 'Medium-Risk Activities',
      count: mediumRisk.length,
      examples: mediumRisk.slice(0, 3).map(a => a.name),
      totalHours: mediumRisk.reduce((sum, a) => sum + (a.baseHours || 0), 0)
    });
  }
  
  if (lowRisk.length > 0) {
    condensed.push({
      category: 'Standard Activities',
      count: lowRisk.length,
      examples: lowRisk.slice(0, 3).map(a => a.name),
      totalHours: lowRisk.reduce((sum, a) => sum + (a.baseHours || 0), 0)
    });
  }
  
  return condensed;
};

/**
 * Generate condensed justification for director/AVP views
 */
export const generateCondensedJustification = (childTeams, activities, totalFTE) => {
  const totalActivities = activities.length;
  const totalManagers = childTeams.length;
  
  const highRiskCount = activities.filter(a => a.risk >= 60).length;
  const complexCount = activities.filter(a => a.comp >= 60).length;
  
  let justification = `${totalManagers} manager team(s) managing ${totalActivities} total activities requiring ${totalFTE.toFixed(2)} FTE. `;
  
  if (highRiskCount > 0) {
    justification += `Includes ${highRiskCount} high-risk programs. `;
  }
  
  if (complexCount > 0) {
    justification += `${complexCount} technically complex programs. `;
  }
  
  justification += 'FTE requirements based on activity complexity, risk levels, and regulatory compliance needs.';
  
  return justification;
};

/**
 * Build hierarchical structure for org chart display
 */
export const buildHierarchyTree = (teams, activities, globalInputs) => {
  // Separate teams by role
  const avpTeams = teams.filter(t => t.role === 'avp');
  const directorTeams = teams.filter(t => t.role === 'director');
  const managerTeams = teams.filter(t => t.role === 'manager');
  
  // Build AVP -> Directors -> Managers structure
  const hierarchy = avpTeams.map(avp => {
    const directors = directorTeams
      .filter(d => d.parentTeamId === avp.id)
      .map(director => {
        const managers = managerTeams
          .filter(m => m.parentTeamId === director.id)
          .map(manager => {
            const teamActivities = activities.filter(a => 
              manager.activities && manager.activities.includes(a.id)
            );
            const fteTotal = calculateTeamFTE(teamActivities, globalInputs);
            
            return {
              team: manager,
              activities: teamActivities,
              fteTotal,
              justification: generateTeamJustification(manager, activities, fteTotal)
            };
          });
        
        const directorActivities = managers.flatMap(m => m.activities);
        const directorFTE = managers.reduce((sum, m) => sum + m.fteTotal, 0);
        
        return {
          team: director,
          managers,
          totalActivities: directorActivities.length,
          totalFTE: directorFTE,
          condensedActivities: condenseActivityList(directorActivities),
          condensedJustification: generateCondensedJustification(managers, directorActivities, directorFTE)
        };
      });
    
    const avpActivities = directors.flatMap(d => d.managers.flatMap(m => m.activities));
    const avpFTE = directors.reduce((sum, d) => sum + d.totalFTE, 0);
    
    return {
      team: avp,
      directors,
      totalActivities: avpActivities.length,
      totalFTE: avpFTE,
      condensedActivities: condenseActivityList(avpActivities),
      condensedJustification: generateCondensedJustification(directors, avpActivities, avpFTE)
    };
  });
  
  return hierarchy;
};
